import {createContext, PropsWithChildren, useState, useCallback, useContext} from 'react';

interface UploadState {
    isUploading: boolean;
    progress: number;       // 0-1, or -1 for indeterminate
    fileName: string | null;
}

interface UploadContextType extends UploadState {
    setUploadState: (state: Partial<UploadState>) => void;
    resetUploadState: () => void;
}

const defaultState: UploadState = {
    isUploading: false,
    progress: 0,
    fileName: null,
};

const defaultContext: UploadContextType = {
    ...defaultState,
    setUploadState: () => {},
    resetUploadState: () => {},
};

const UploadContext = createContext<UploadContextType>(defaultContext);

export const UploadProvider = ({children}: PropsWithChildren) => {
    const [state, setState] = useState<UploadState>(defaultState);

    const setUploadState = useCallback((partial: Partial<UploadState>) => {
        setState(prev => ({...prev, ...partial}));
    }, []);

    const resetUploadState = useCallback(() => {
        setState(defaultState);
    }, []);

    return (
        <UploadContext.Provider value={{...state, setUploadState, resetUploadState}}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUploadState = () => useContext(UploadContext);
